import React, { useState } from "react";
import Button from "./Button";
import { FormattedMessage } from "react-intl";
import Markdown from "./Markdown";
import { Controller, useForm } from "react-hook-form";
import MarkdownEditor from "./MarkdownEditor";
import FileUploadPlugin from "./MarkdownEditor/plugins/FileUpload";
import SuggestionPlugin from "./MarkdownEditor/plugins/Suggestion";
import { ErrorMessage } from "./FormGroup";

interface SubmitData {
  content: string;
}

interface Props {
  id: ID;
  onSubmit: (data: SubmitData) => Promise<void>;
  canUploadFiles?: boolean;
  canUseSuggestions?: boolean;
  className?: string;
  title?: React.ReactNode;
  fallback?: React.ReactNode;
  canEdit?: boolean;
  disabled?: boolean;
  content?: string;
  maxLength?: number;
}

type FormData = {
  value: string;
};

export default function EditMarkdownSection({
  id,
  title,
  onSubmit,
  fallback,
  content = "",
  canEdit = false,
  disabled = false,
  canUploadFiles = false,
  canUseSuggestions = true,
  maxLength,
}: Props) {
  const {
    handleSubmit,
    reset,
    control,
    formState: { dirtyFields },
  } = useForm<FormData>();
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);

  const resetForm = () => {
    reset();
    setFormError(undefined);
    setIsEditing(false);
  };

  const onUpdate = handleSubmit((data) => {
    if (disabled) {
      return;
    }
    if (data.value === content) {
      setIsEditing(false);
      return;
    }
    if (content && !dirtyFields.value) {
      return;
    }

    onSubmit({ content: data.value.trim() })
      .then(resetForm)
      .catch((err) => setFormError(String(err)));
  });

  const nonEditingContent = content ? <Markdown>{content}</Markdown> : fallback;
  return (
    <form onSubmit={onUpdate}>
      <div className="flex flex-row justify-between space-x-2">
        <div className="flex-grow">{title}</div>
        {canEdit &&
          (isEditing ? (
            <div className="flex flex-row flex-grow-0 space-x-2 self-center">
              <Button
                kind="text"
                onClick={() => setIsEditing(false)}
                disabled={disabled}
              >
                <FormattedMessage defaultMessage="Cancel" />
              </Button>
              <Button kind="primary" disabled={disabled} onClick={onUpdate}>
                <FormattedMessage defaultMessage="Save" />
              </Button>
            </div>
          ) : (
            <Button kind="text" onClick={() => setIsEditing(true)}>
              <FormattedMessage defaultMessage="Edit" />
            </Button>
          ))}
      </div>
      {formError && <ErrorMessage error={formError} />}
      <div className="pb-16">
        {isEditing ? (
          <Controller
            name="value"
            control={control}
            rules={{ maxLength }}
            defaultValue={content}
            render={({ field }) => (
              <MarkdownEditor {...field} namespaceId={id}>
                {canUploadFiles && <FileUploadPlugin />}
                {canUseSuggestions && (
                  <SuggestionPlugin source={{ member: id }} />
                )}
              </MarkdownEditor>
            )}
          />
        ) : (
          nonEditingContent
        )}
      </div>
    </form>
  );
}
